/**
 * Slash marks — arc-shaped slashes along the void edge, with proximity bias
 * and burst spawning for foreshadow/consume events.
 */

import type { Graphics } from 'pixi.js';
import { VOID } from '../../constants';
import type { VoidState, SlashMark } from './types';
import { visibleX, lerp } from './types';
import { getEdgeY } from './edge';

/** Spawn a cluster of slash marks at a given x position (used by triggerForeshadow/triggerConsumeBurst). */
export function spawnSlashMarksAt(
  x: number, voidY: number, count: number, spread: number,
  lifeMult: number, arcMult: number, alphaMin: number, alphaMax: number,
  state: VoidState,
): void {
  for (let i = 0; i < count; i++) {
    const sx = x + (Math.random() - 0.5) * spread;

    const colorRoll = Math.random();
    let color: number;
    if (colorRoll < 0.50) color = VOID.EDGE_COLOR;
    else if (colorRoll < 0.70) color = 0xffffff;
    else if (colorRoll < 0.85) color = 0xffaaff;
    else color = 0x220033;

    const life = (VOID.SLASH_LIFE_MIN + Math.random() * (VOID.SLASH_LIFE_MAX - VOID.SLASH_LIFE_MIN)) * lifeMult;

    state.slashMarks.push({
      x: sx,
      baseY: getEdgeY(sx, voidY, state),
      arcAngle: -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.6,
      arcRadius: (VOID.SLASH_ARC_RADIUS_MIN + Math.random() * (VOID.SLASH_ARC_RADIUS_MAX - VOID.SLASH_ARC_RADIUS_MIN)) * arcMult,
      arcSweep: VOID.SLASH_ARC_SWEEP_MIN + Math.random() * (VOID.SLASH_ARC_SWEEP_MAX - VOID.SLASH_ARC_SWEEP_MIN),
      life,
      maxLife: life,
      color,
      width: VOID.SLASH_WIDTH_MIN + Math.random() * (VOID.SLASH_WIDTH_MAX - VOID.SLASH_WIDTH_MIN),
      alpha: alphaMin + Math.random() * (alphaMax - alphaMin),
    });
  }
}

export function updateSlashMarks(gfx: Graphics, dt: number, voidY: number, state: VoidState): void {
  const maxSlash = state.riseIntensity > 0.1
    ? Math.round(lerp(VOID.SLASH_MAX_CALM, VOID.SLASH_MAX_RISE, state.riseIntensity))
    : VOID.SLASH_MAX_CALM;
  const spawnRate = state.riseIntensity > 0.1
    ? lerp(VOID.SLASH_SPAWN_RATE_CALM, VOID.SLASH_SPAWN_RATE_RISE, state.riseIntensity)
    : VOID.SLASH_SPAWN_RATE_CALM;

  // Spawn
  if (state.slashMarks.length < maxSlash && Math.random() < spawnRate) {
    let x: number;

    // Proximity bias: 40% chance to spawn near closest alive player
    const closePlayers = state.playerPositions.filter(p => {
      if (!p.alive) return false;
      const dy = p.y - voidY;
      return dy < VOID.PROXIMITY_Y_RANGE && dy > -VOID.PROXIMITY_Y_RANGE;
    });

    if (closePlayers.length > 0 && Math.random() < VOID.SLASH_PROXIMITY_BIAS) {
      const target = closePlayers[Math.floor(Math.random() * closePlayers.length)];
      x = target.x + (Math.random() - 0.5) * VOID.PROXIMITY_X_RANGE * 2;
    } else {
      const sv = visibleX();
      x = sv.off + Math.random() * sv.w;
    }

    // Color mix: 50% magenta, 20% white, 15% light magenta, 15% dark
    const colorRoll = Math.random();
    let color: number;
    if (colorRoll < 0.50) color = VOID.EDGE_COLOR;        // magenta
    else if (colorRoll < 0.70) color = 0xffffff;          // white
    else if (colorRoll < 0.85) color = 0xffaaff;          // light magenta
    else color = 0x220033;                                  // dark contrast

    const life = VOID.SLASH_LIFE_MIN + Math.random() * (VOID.SLASH_LIFE_MAX - VOID.SLASH_LIFE_MIN);

    state.slashMarks.push({
      x,
      baseY: getEdgeY(x, voidY, state),
      arcAngle: -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.6, // upward-biased
      arcRadius: VOID.SLASH_ARC_RADIUS_MIN + Math.random() * (VOID.SLASH_ARC_RADIUS_MAX - VOID.SLASH_ARC_RADIUS_MIN),
      arcSweep: VOID.SLASH_ARC_SWEEP_MIN + Math.random() * (VOID.SLASH_ARC_SWEEP_MAX - VOID.SLASH_ARC_SWEEP_MIN),
      life,
      maxLife: life,
      color,
      width: VOID.SLASH_WIDTH_MIN + Math.random() * (VOID.SLASH_WIDTH_MAX - VOID.SLASH_WIDTH_MIN),
      alpha: VOID.SLASH_ALPHA_MIN + Math.random() * (VOID.SLASH_ALPHA_MAX - VOID.SLASH_ALPHA_MIN),
    });
  }

  // Update + draw
  gfx.clear();
  for (let i = state.slashMarks.length - 1; i >= 0; i--) {
    const s = state.slashMarks[i];
    s.life -= dt;
    if (s.life <= 0) {
      state.slashMarks.splice(i, 1);
      continue;
    }

    const lifeRatio = s.life / s.maxLife;
    // Fade in and out: sin curve peaks at 0.5 life
    const fadeAlpha = s.alpha * Math.sin(lifeRatio * Math.PI);

    const offset = 5 + s.arcRadius * 0.3; // above edge
    const cx = s.x;
    const cy = s.baseY - offset;
    const startAngle = s.arcAngle - s.arcSweep / 2;
    const endAngle = s.arcAngle + s.arcSweep / 2;

    // Main arc
    gfx.arc(cx, cy, s.arcRadius, startAngle, endAngle);
    gfx.stroke({ color: s.color, width: s.width, alpha: fadeAlpha });

    // Luminous inner core for white/magenta slashes
    if (s.color === 0xffffff || s.color === VOID.EDGE_COLOR) {
      gfx.arc(cx, cy, s.arcRadius, startAngle, endAngle);
      gfx.stroke({ color: 0xffffff, width: Math.max(0.5, s.width * 0.4), alpha: fadeAlpha * 0.6 });
    }
  }
}
